// src/components/risk/FounderMetricsBanner.jsx
// Banner showing intervention success metrics for founder dashboard

import React from 'react';
import { TrendingUp, TrendingDown, Users, Shield, Award, Target } from 'lucide-react';

const FounderMetricsBanner = ({
  savedClientsThisMonth = 0,
  savedClientsLastMonth = 0,
  totalInterventions = 0,
  successRate = 0,
  avgRiskScoreAtIntervention = 0,
  className = ''
}) => {
  // Calculate change percentage
  const changePercent = savedClientsLastMonth > 0
    ? ((savedClientsThisMonth - savedClientsLastMonth) / savedClientsLastMonth * 100).toFixed(0)
    : savedClientsThisMonth > 0 ? 100 : 0;
  const isPositive = savedClientsThisMonth >= savedClientsLastMonth;

  return (
    <div className={`bg-gradient-to-r from-amber-900/30 via-amber-800/20 to-amber-900/30 border border-amber-500/30 rounded-xl p-4 ${className}`}>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        {/* Main metric - Saved Clients */}
        <div className="flex items-center gap-4">
          <div className="p-3 bg-amber-500/20 rounded-xl">
            <Shield className="w-8 h-8 text-amber-400" />
          </div>
          <div>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-bold text-white">{savedClientsThisMonth}</span>
              <span className="text-lg text-slate-400">clients saved</span>
            </div>
            <div className="flex items-center gap-1 mt-1">
              {isPositive ? (
                <TrendingUp className="w-4 h-4 text-green-400" />
              ) : (
                <TrendingDown className="w-4 h-4 text-red-400" />
              )}
              <span className={`text-sm font-medium ${isPositive ? 'text-green-400' : 'text-red-400'}`}>
                {changePercent > 0 ? '+' : ''}{changePercent}%
              </span>
              <span className="text-sm text-slate-500">vs last month</span>
            </div>
          </div>
        </div>

        {/* Secondary metrics */}
        <div className="flex flex-wrap gap-6">
          {/* Success Rate */}
          <div className="flex items-center gap-2">
            <div className="p-2 bg-slate-700/50 rounded-lg">
              <Target className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <div className="text-lg font-semibold text-white">
                {typeof successRate === 'number' ? `${successRate.toFixed(1)}%` : successRate}
              </div>
              <div className="text-xs text-slate-400">Success Rate</div>
            </div>
          </div>

          {/* Total Interventions */}
          <div className="flex items-center gap-2">
            <div className="p-2 bg-slate-700/50 rounded-lg">
              <Users className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <div className="text-lg font-semibold text-white">{totalInterventions}</div>
              <div className="text-xs text-slate-400">Interventions</div>
            </div>
          </div>

          {/* Avg Risk Score at Intervention */}
          <div className="flex items-center gap-2">
            <div className="p-2 bg-slate-700/50 rounded-lg">
              <Award className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <div className="text-lg font-semibold text-white">
                {typeof avgRiskScoreAtIntervention === 'number' ? avgRiskScoreAtIntervention.toFixed(1) : avgRiskScoreAtIntervention}
              </div>
              <div className="text-xs text-slate-400">Avg Risk Score at Intervention</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Compact version for sidebar/widget
export const FounderMetricsMini = ({ savedCount = 0, className = '' }) => (
  <div className={`flex items-center gap-2 px-3 py-2 bg-amber-500/10 border border-amber-500/30 rounded-lg ${className}`}>
    <Shield className="w-4 h-4 text-amber-400" />
    <span className="text-sm text-amber-400 font-medium">{savedCount} clients saved this month</span>
  </div>
);

export default FounderMetricsBanner;
